import React from 'react';
import { Box, Button, LinearProgress } from '@material-ui/core';
import PlayIcon from '@material-ui/icons/PlayArrow';
import PauseIcon from '@material-ui/icons/Pause';
import { PiMusicNotes, PiMusicNote } from 'react-icons/pi';
import { motion } from 'framer-motion';
import PlayerBottomSheet from './PlayerBottomSheet';
import { useTheme } from '../contexts/theme';
import { useExperience } from '../contexts/experience';
import { useLanguage } from '../contexts/language';

const AnimatedSpan = ({ startAnimation, children }) => {
  const baseStyle = {
    display: 'inline-flex',
  };

  if (!startAnimation) {
    return (
      <span style={baseStyle} aria-hidden="true">
        {children}
      </span>
    );
  }

  return (
    <motion.span
      style={baseStyle}
      animate={{ scale: [1, 1.3, 1, 1.3, 1], rotate: [0, 360, 360, 360, 0] }}
      transition={{ repeat: Infinity, duration: 3, ease: 'backInOut' }}
      aria-hidden="true"
    >
      {children}
    </motion.span>
  );
};

const LinearProgresBar = ({ audio }) => {
  const [seek, setSeek] = React.useState(0);
  const { content } = useLanguage();

  // eslint-disable-next-line
  const duration = audio?._duration;
  const progress = (seek / duration) * 100;

  React.useEffect(() => {
    if (!audio) return;
    const seekInterval = setInterval(() => {
      try {
        const currentSeek = audio.seek();
        setSeek(currentSeek);
      } catch (error) {
        // do nothing
      }
    }, 100);
    return () => clearInterval(seekInterval);
  }, [audio, duration]);

  return (
    <Box width="100%">
      <LinearProgress
        color="secondary"
        variant="determinate"
        value={progress}
        role="progressbar"
        aria-valuenow={Math.round(progress)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={content('aria_audio_playback_progress')}
      />
    </Box>
  );
};

const PlayerOnMap = () => {
  const { styles } = useTheme();
  const { content } = useLanguage();
  const { isActive, active, fetching } = useExperience();
  const isFetching = React.useMemo(() => fetching && fetching.length > 0, [fetching]);

  const [openSheet, setOpenSheet] = React.useState(false);
  const [lastActive, setLastActive] = React.useState([]);
  const triggerButtonRef = React.useRef(null);

  const singleSpotActive = active?.length === 1;
  const someIsPlaying = active?.some((spot) => spot.audio.playing());
  const primary = styles.palette.primary.main;

  React.useEffect(() => {
    if (!isActive) {
      setLastActive([]);
      setOpenSheet(false);
    }

    if (isActive && active.length >= lastActive.length) {
      setLastActive(active);
      setOpenSheet(true);
    }
  }, [isActive, active]);

  // open when fetching to show loader
  React.useEffect(() => {
    if (isFetching) {
      setOpenSheet(true);
    }
  }, [fetching, isFetching]);

  const getLabelText = React.useCallback(() => {
    if (!active?.length) return null;
    return singleSpotActive ? active[0]?.label || '' : content('multiple_agami_active');
  }, [active, singleSpotActive, content]);

  const labelText = React.useMemo(() => getLabelText(), [getLabelText]);

  return (
    <>
      <PlayerBottomSheet openSheet={openSheet} setOpenSheet={setOpenSheet} triggerRef={triggerButtonRef} />

      <Box
        position="absolute"
        width="100%"
        bgcolor={primary}
        bottom={0}
        borderRadius="5px 5px 0 0"
        zIndex={999}
        aria-hidden={!isActive || openSheet}
        style={{
          opacity: isActive ? 1 : 0,
          pointerEvents: isActive ? 'all' : 'none',
          transform: isActive ? 'scale(1) translate(0px, 0px)' : 'scale(0.8) translate(0px, 100px)',
          transition: 'all .5s ease-in-out',
        }}
      >
        <Button
          ref={triggerButtonRef}
          color="primary"
          variant="contained"
          fullWidth
          onClick={() => setOpenSheet(true)}
          aria-haspopup="dialog"
          aria-expanded={openSheet}
          aria-label={content('aria_open_audio_player')}
          tabIndex={isActive ? 0 : -1}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingLeft: '1rem',
            paddingRight: '1rem',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 4,
            }}
          >
            <AnimatedSpan startAnimation={someIsPlaying}>
              {singleSpotActive ? (
                <PiMusicNote style={{ fontSize: '1.5rem' }} />
              ) : (
                <PiMusicNotes style={{ fontSize: '1.5rem' }} />
              )}
            </AnimatedSpan>

            {labelText && (
              <span aria-live="polite" style={{ marginLeft: 4 }}>
                {labelText}
              </span>
            )}
          </span>

          {someIsPlaying ? (
            <PauseIcon style={{ fontSize: '2rem' }} aria-hidden="true" />
          ) : (
            <PlayIcon style={{ fontSize: '2rem' }} aria-hidden="true" />
          )}
        </Button>

        {singleSpotActive && <LinearProgresBar audio={active[0]?.audio} />}
      </Box>
    </>
  );
};

export default PlayerOnMap;
